// How a live hook waits for its data without the widget changing (track2-plan.md §1,
// T2-1 fork 1A). The hook asks with `useLiveQuery(key, load)`: the first time, `load()`
// runs and React's `use()` SUSPENDS the widget — the shared <LiveBoundary> around every
// module (bridge.tsx) shows "Loading real data…" — and when the answer lands the widget
// renders as if the data had always been there, exactly like the synchronous snapshot hook.
//
// Each widget keeps ONLY ITS OWN answers (keyed by its module element), so its own
// re-renders (a sort, a resize, a refresh) don't ask again. Another widget, a reload or a
// Retry asks afresh — no sharing between widgets: that's a Track 3 speed-up, and leaving it
// out keeps the Track 2 measurements honest (every request's real cost shows).

import { createContext, use } from "react";
import { progressFinished, progressReset, progressStarted } from "./load-progress.js";

/** The module element of the widget being rendered — provided by <LiveBoundary>. */
export const LiveModuleContext = createContext<HTMLElement | null>(null);

const answers = new WeakMap<HTMLElement, Map<string, Promise<unknown>>>();

// Calls still on their way, page-wide. The undo net (canvas/undo.ts) waits for these: a
// live widget's change (a season pick) only lands when its data does, seconds after the
// click — deciding "did anything change?" two frames after the click would miss it and
// lose the Undo step. On the snapshot site this stays 0, so the net runs as before.
let inFlight = 0;
let waiters: (() => void)[] = [];

/** True while any live call is on its way. */
export function liveBusy(): boolean {
  return inFlight > 0;
}

/** Resolves once no live call is on its way (at once if none is). */
export function whenLiveSettled(): Promise<void> {
  return inFlight === 0 ? Promise.resolve() : new Promise((resolve) => waiters.push(resolve));
}

/** Count `p` as on its way until it settles (exported for the unit test). */
export function trackLive<T>(p: Promise<T>): Promise<T> {
  inFlight++;
  const done = () => {
    inFlight--;
    if (inFlight === 0) {
      const ready = waiters;
      waiters = [];
      for (const w of ready) w();
    }
  };
  p.then(done, done);
  return p;
}

/** The widget's answer for `key`, fetching it with `load` the first time (suspends).
 *  A null key = nothing to ask (e.g. no league chosen) → null, so callers never call
 *  this conditionally. */
export function useLiveQuery<T>(key: string | null, load: () => Promise<T>): T | null {
  if (key === null) return null;
  // `use`, not useContext: React allows it after the early return above.
  return use(answerFor(use(LiveModuleContext), key, load));
}

/** Several answers at once (a Stats comparison: one per player). Every call starts before
 *  the widget waits, so 5 players take one wait, not five in a row (useLiveQuery in a loop
 *  would wait for each before asking the next). */
export function useLiveQueries<T>(queries: readonly { key: string; load: () => Promise<T> }[]): T[] {
  if (queries.length === 0) return [];
  const mod = use(LiveModuleContext);
  const pending = queries.map((q) => answerFor(mod, q.key, q.load));
  return pending.map((p) => use(p));
}

function answerFor<T>(mod: HTMLElement | null, key: string, load: () => Promise<T>): Promise<T> {
  if (!mod) throw new Error(`useLiveQuery("${key}") outside a <LiveBoundary> — live hooks only run inside a module.`);
  let mine = answers.get(mod);
  if (!mine) {
    mine = new Map();
    answers.set(mod, mine);
  }
  let pending = mine.get(key) as Promise<T> | undefined;
  if (!pending) {
    pending = trackLive(load());
    mine.set(key, pending);
    // For the loading screen's "step 2 of 3" (load-progress.ts).
    progressStarted(mod, key);
    const finished = () => progressFinished(mod, key);
    pending.then(finished, finished);
  }
  return pending;
}

/** Forget a widget's answers — Retry calls it, so the next render asks the API again. */
export function forgetLiveAnswers(mod: HTMLElement): void {
  answers.delete(mod);
  progressReset(mod);
}
